import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Form, FormField, FormItemLayout } from '@/components/ui/form'
import { InputPassword } from '@/components/ui/input'
import { AuthCard } from '@/components/view/auth-view'

import { authNewPassword } from '@/services/auth/auth.gql'

export const Route = createFileRoute('/auth/new-password/$token')({
  beforeLoad({ params }) {
    if (!params.token) {
      throw redirect({ to: '/auth/login' })
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <AuthCard title="Reset Password">
      <ResetForm />
    </AuthCard>
  )
}

function ResetForm() {
  const navigate = Route.useNavigate()
  const params = Route.useParams()
  const form = useForm<FormDto>({
    resolver: zodResolver(FormDto),
    defaultValues: getDefaultValues(),
  })

  const { mutateAsync } = useMutation({
    mutationFn: async (data: FormDto) => {
      const res = await authNewPassword.api({
        req: { token: params.token, new_password: data.password },
      })

      if (res.resetPassword.success) {
        return data
      }

      throw new Error('Gagal memperbarui password')
    },
    onSuccess() {
      navigate({ to: '/auth/login' })
    },
  })

  const handleSubmit = form.handleSubmit((data) => {
    toast.promise(mutateAsync(data), {
      loading: 'Memperbarui password',
      success: 'Berhasil memperbarui password',
      error: 'Gagal memperbarui password',
    })
  }, console.error)

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit}>
        <fieldset className="space-y-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItemLayout
                label="Password Baru"
                className="min-w-[250px] grow"
              >
                <InputPassword placeholder="Masukkan Password" {...field} />
              </FormItemLayout>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItemLayout
                label="Konfirmasi Password Baru"
                className="min-w-[250px] grow"
              >
                <InputPassword
                  placeholder="Masukkan ulang password"
                  {...field}
                />
              </FormItemLayout>
            )}
          />
          <Button variant="yes" className="w-full font-bold">
            Reset Password
          </Button>
        </fieldset>
      </form>
    </Form>
  )
}

type FormDto = z.infer<typeof FormDto>
const FormDto = z
  .object({
    password: z.string().min(1),
    confirmPassword: z.string().min(1),
  })
  .superRefine(({ password, confirmPassword }, ctx) => {
    if (!!password && password !== confirmPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['passwordConfirmation'],
        message: 'Password konfirmasi harus sama',
      })
    }
  })

function getDefaultValues(): FormDto {
  return {
    password: '',
    confirmPassword: '',
  }
}
